import React, { useEffect, useState, useRef, useMemo, useCallback, memo } from 'react';
import ReactDOM from 'react-dom';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Briefcase,
  TrendingUp,
  BarChart2,
  Shield,
  User,
  GitBranch,
  FolderOpen,
  Building2,
  Zap,
  ClipboardList,
  UserCircle,
  GraduationCap,
  Settings,
  Bell,
  MessageSquare,
  AlertTriangle,
  FileText,
  ChevronLeft,
  ChevronRight,
  Layers,
  Upload,
  LogOut,
} from 'lucide-react';
import clsx from 'clsx';
import useAuthStore from '../../store/authStore';
import Avatar from '../ui/Avatar';
import './Sidebar.css';

/* ── Helper functions ──────────────────────────────────────────────────── */
function getInitials(email) {
  if (!email) return '?';
  const parts = email.split('@')[0].split(/[._-]/);
  return parts.slice(0, 2).map(p => p[0]).join('').toUpperCase() || '?';
}

/* ── Icon maps ─────────────────────────────────────────────────────────── */
const ICON_MAP = {
  LayoutDashboard, Users, GitBranch, FolderOpen, Building2, Zap,
  ClipboardList, UserCircle, GraduationCap, BarChart2, Settings,
  User, Bell, MessageSquare, AlertTriangle, FileText, Upload,
};

const GROUP_ICON_MAP = {
  'overview':            LayoutDashboard,
  'resource-management': Users,
  'demand-delivery':     Briefcase,
  'quality-growth':      TrendingUp,
  'reports-invoicing':   BarChart2,
  'administration':      Shield,
  'self-service':        User,
};

/* ── Nav data ──────────────────────────────────────────────────────────── */
const NAV_ITEMS = [
  { key: 'dashboard',    path: '/dashboard',    label: 'Dashboard',       icon: 'LayoutDashboard', roles: ['Super Admin','Ops Admin','RMG Lead','PMO Team','DU Head','HR','Project Manager','Finance','Training Team','Admin','PMO Lead','PMO Executive','PM','TTD Executive'], activePaths: ['/dashboard'] },
  { key: 'bench',        path: '/bench',        label: 'Bench Pool',      icon: 'Users',           roles: ['Super Admin','Ops Admin','RMG Lead','PMO Team','DU Head'], activePaths: ['/bench'] },
  { key: 'pipeline',     path: '/pipeline',     label: 'Pipeline',        icon: 'GitBranch',       roles: ['Super Admin','Ops Admin','PMO Team','RMG Lead','DU Head','PM'], activePaths: ['/pipeline'] },
  { key: 'indents',      path: '/indents',      label: 'Indents',         icon: 'FileText',        roles: ['Super Admin','Ops Admin','PMO Team','RMG Lead','DU Head','PM'], activePaths: ['/indents'] },
  { key: 'interviews',   path: '/interviews',   label: 'Interview',       icon: 'ClipboardList',   roles: ['Super Admin','Ops Admin','PMO Team','RMG Lead','DU Head','PM'], activePaths: ['/interviews'] },
  { key: 'pm-feedback',  path: '/pm-feedback',  label: 'PM Feedback',     icon: 'MessageSquare',   roles: ['Super Admin','PM','PMO Team','RMG Lead','DU Head','HR'], activePaths: ['/pm-feedback'] },
  { key: 'idt-applications', path: '/idt-applications', label: 'IDT Applications', icon: 'FileText', roles: ['Super Admin','Ops Admin','PMO Team','RMG Lead','DU Head'], activePaths: ['/idt-applications'] },
  { key: 'pattern-flags',path: '/pattern-flags',label: 'Pattern Flags',   icon: 'AlertTriangle',   roles: ['Super Admin','Ops Admin','PMO Team','RMG Lead','HR'], activePaths: ['/pattern-flags'] },
  { key: 'projects',     path: '/projects',     label: 'Projects',        icon: 'FolderOpen',      roles: ['Super Admin','Ops Admin','RMG Lead','PMO Team','Finance'], activePaths: ['/projects'] },
  { key: 'customers',    path: '/customers',    label: 'Customers',       icon: 'Building2',       roles: ['Super Admin','Ops Admin','RMG Lead','PMO Team','Finance','DU Head','Project Manager'], activePaths: ['/customers'] },
  { key: 'skill-match',  path: '/skill-match',  label: 'Skill Match',     icon: 'Zap',             roles: ['Super Admin','Ops Admin','PMO Team','RMG Lead'], activePaths: ['/skill-match'] },
  { key: 'employees',    path: '/employees',    label: 'Employees',       icon: 'UserCircle',      roles: ['Super Admin','Ops Admin','RMG Lead','PMO Team','DU Head','HR','Training Team'], activePaths: ['/employees'] },
  { key: 'training',     path: '/training',     label: 'Training',        icon: 'GraduationCap',   roles: ['Super Admin','Ops Admin','PMO Team','RMG Lead','DU Head','HR','Training Team'], activePaths: ['/training','/training/new','/training/form'] },
  { key: 'reports',      path: '/reports',      label: 'Reports',         icon: 'BarChart2',       roles: ['Super Admin','Ops Admin','Finance','Admin','PMO Team','PMO Lead','PMO Executive'], activePaths: ['/reports'] },
  { key: 'invoicing',    path: '/invoicing',    label: 'Invoicing',       icon: 'FileText',        roles: ['Super Admin','Ops Admin','Finance','Admin','PMO Team','PMO Lead','PMO Executive'], activePaths: ['/invoicing'], disabled: true },
  { key: 'notifications',path: '/notifications',label: 'Alerts',          icon: 'Bell',            roles: ['all'], activePaths: ['/notifications'] },
  { key: 'admin-users',      path: '/admin/users',       label: 'User Management', icon: 'UserCircle', roles: ['Super Admin', 'Admin', 'Ops Admin', 'RMG Lead', 'RMG Team', 'PMO Team', 'PMO Lead'], activePaths: ['/admin/users'] },
  { key: 'admin-sub-status', path: '/admin/sub-status',  label: 'Sub-Status',      icon: 'Settings',   roles: ['Super Admin', 'Admin', 'Ops Admin', 'RMG Lead', 'RMG Team', 'PMO Team', 'PMO Lead'], activePaths: ['/admin/sub-status'] },
  { key: 'resume-upload',    path: '/admin/resumes',     label: 'Resume Upload',   icon: 'Upload',     roles: ['Super Admin', 'Admin', 'Ops Admin', 'PMO Team', 'PMO Lead', 'PMO Executive', 'RMG Lead', 'RMG Team', 'RMG_APPROVER'], activePaths: ['/admin/resumes'] },
  { key: 'admin-allocation-history', path: '/admin/allocation-history', label: 'Allocation History', icon: 'ClipboardList', roles: ['Super Admin', 'Admin', 'Ops Admin', 'RMG Lead', 'RMG Team', 'RMG_APPROVER', 'PMO Team', 'PMO Lead', 'PMO Executive'], activePaths: ['/admin/allocation-history'] },
  { key: 'admin',        path: '/admin',        label: 'Admin',           icon: 'Settings',        roles: ['Super Admin', 'Admin', 'Ops Admin', 'RMG Lead', 'RMG Team', 'PMO Team', 'PMO Lead'], activePaths: ['/admin'] },
  { key: 'portal',       path: '/portal',       label: 'My Portal',       icon: 'User',            roles: ['Employee'], activePaths: ['/portal'] },
];

const NAV_GROUPS = [
  { id: 'overview',            label: 'Overview',             itemKeys: ['dashboard','notifications'] },
  { id: 'resource-management', label: 'Resource Management',  itemKeys: ['interviews','pattern-flags','bench','skill-match','employees'] },
  { id: 'demand-delivery',     label: 'Demand & Delivery',    itemKeys: ['pipeline','indents','projects','customers'] },
  { id: 'quality-growth',      label: 'Quality & Growth',     itemKeys: ['pm-feedback','training','idt-applications'] },
  { id: 'reports-invoicing',   label: 'Reports & Invoicing',  itemKeys: ['reports','invoicing'] },
  { id: 'administration',      label: 'Administration',       itemKeys: ['admin-users','resume-upload','admin-sub-status','admin-allocation-history'] },
  { id: 'self-service',        label: 'Self Service',         itemKeys: ['portal'] },
];

/* ── Helpers ───────────────────────────────────────────────────────────── */
function hasAccess(item, userRoles) {
  if (item.roles.includes('all')) return true;
  if (!userRoles || userRoles.length === 0) return false;
  return userRoles.some((r) => {
    const rName = typeof r === 'string' ? r.toLowerCase() : '';
    return item.roles.some((allowed) => allowed.toLowerCase() === rName);
  });
}

function isItemActive(item, pathname) {
  return (item.activePaths || [item.path]).some(
    (p) => pathname === p || pathname.startsWith(`${p}/`)
  );
}

const GroupFlyout = memo(function GroupFlyout({ group, anchorRect, pathname, onClose }) {
  if (!anchorRect) return null;

  const top  = anchorRect.top;
  const left = anchorRect.right + 8; // 8px gap from sidebar edge

  return ReactDOM.createPortal(
    <div
      className="sidebar__flyout-portal"
      style={{ top, left }}
      onMouseLeave={onClose}
    >
      {/* arrow pointing left */}
      <div className="sidebar__flyout-arrow" />

      <div className="sidebar__flyout-title">{group.label}</div>

      <ul className="sidebar__flyout-list">
        {group.items.map((item) => {
          const Icon = ICON_MAP[item.icon];
          const active = isItemActive(item, pathname);
          if (item.disabled) {
            return (
              <li key={item.path}>
                <div
                  className="sidebar__flyout-link sidebar__flyout-link--disabled"
                  title={`${item.label} coming soon`}
                  onClick={(e) => e.preventDefault()}
                >
                  {Icon && <Icon size={15} aria-hidden="true" />}
                  <span>{item.label}</span>
                </div>
              </li>
            );
          }
          return (
            <li key={item.path}>
              <NavLink
                to={item.path}
                className={clsx('sidebar__flyout-link', {
                  'sidebar__flyout-link--active': active,
                })}
                onClick={onClose}
              >
                {Icon && <Icon size={15} aria-hidden="true" />}
                <span>{item.label}</span>
              </NavLink>
            </li>
          );
        })}
      </ul>
    </div>,
    document.body
  );
});

const UserMenuDropdown = memo(function UserMenuDropdown({ anchorRect, user, onLogout, onClose }) {
  if (!anchorRect) return null;

  const top  = anchorRect.top - 100;
  const left = anchorRect.right + 8;

  return ReactDOM.createPortal(
    <div
      className="sidebar__flyout-portal sidebar__user-menu-flyout"
      style={{ top, left }}
      onMouseLeave={onClose}
    >
      <div className="sidebar__flyout-arrow" />

      <div className="sidebar__user-menu-header">
        <div className="sidebar__user-menu-email">{user?.email}</div>
      </div>

      <ul className="sidebar__user-menu-list">
        <li>
          <button
            className="sidebar__user-menu-item sidebar__user-menu-item--logout"
            onClick={() => {
              onLogout();
              onClose();
            }}
          >
            <LogOut size={15} aria-hidden="true" />
            <span>Sign out</span>
          </button>
        </li>
      </ul>
    </div>,
    document.body
  );
});

const CollapsedGroupItem = memo(function CollapsedGroupItem({ group, pathname, onLinkClick }) {
  const [anchorRect, setAnchorRect] = useState(null);
  const rowRef  = useRef(null);
  const timerRef = useRef(null);
  const GroupIcon = GROUP_ICON_MAP[group.id] || Layers;
  const hasActive = useMemo(() => group.items.some((item) => isItemActive(item, pathname)), [group.items, pathname]);

  const open = useCallback(() => {
    clearTimeout(timerRef.current);
    if (rowRef.current) setAnchorRect(rowRef.current.getBoundingClientRect());
  }, []);

  const close = useCallback(() => {
    timerRef.current = setTimeout(() => setAnchorRect(null), 100);
  }, []);

  const handleFlyoutClose = useCallback(() => {
    clearTimeout(timerRef.current);
    setAnchorRect(null);
    onLinkClick();
  }, [onLinkClick]);

  return (
    <li
      ref={rowRef}
      className={clsx('sidebar__collapsed-group', {
        'sidebar__collapsed-group--active': hasActive,
      })}
      onMouseEnter={open}
      onMouseLeave={close}
    >
      <button
        className="sidebar__collapsed-group-btn"
        aria-label={group.label}
        title={group.label}
        onClick={open}
      >
        <GroupIcon size={20} aria-hidden="true" />
      </button>

      {anchorRect && (
        <GroupFlyout
          group={group}
          anchorRect={anchorRect}
          pathname={pathname}
          onClose={handleFlyoutClose}
        />
      )}
    </li>
  );
});

function Sidebar({ isOpen, onClose, isCollapsed, onToggleCollapse, notificationData = {} }) {
  const { user, clearAuth } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();
  const userRoles = user?.roles || [];
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const [anchorRect, setAnchorRect] = useState(null);
  const timerRef = useRef(null);

  const { visibleGroups, activeGroupId } = useMemo(() => {
    const visibleItems = NAV_ITEMS.filter((item) => hasAccess(item, userRoles));
    const byKey = Object.fromEntries(visibleItems.map((item) => [item.key, item]));
    const groups = NAV_GROUPS
      .map((g) => ({ ...g, items: g.itemKeys.map((k) => byKey[k]).filter(Boolean) }))
      .filter((g) => g.items.length > 0);

    const activeId = groups.find((g) =>
      g.items.some((item) => isItemActive(item, location.pathname))
    )?.id || null;

    return { visibleGroups: groups, activeGroupId: activeId };
  }, [userRoles, location.pathname]);

  const [expandedGroupId, setExpandedGroupId] = useState(activeGroupId);
  useEffect(() => { setExpandedGroupId(activeGroupId); }, [activeGroupId]);

  const renderLink = useCallback((item) => {
    const Icon = ICON_MAP[item.icon];
    const active = isItemActive(item, location.pathname);

    if (item.disabled) {
      return (
        <div
          key={item.path}
          className="sidebar__link sidebar__link--disabled"
          title={`${item.label} coming soon`}
          onClick={(e) => e.preventDefault()}
        >
          {Icon && <Icon size={18} className="sidebar__icon" aria-hidden="true" />}
          <span className="sidebar__label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            {item.label}
          </span>
        </div>
      );
    }

    return (
      <NavLink
        to={item.path}
        className={clsx('sidebar__link', { 'sidebar__link--active': active })}
        onClick={() => window.innerWidth < 768 && onClose()}
      >
        {Icon && <Icon size={18} className="sidebar__icon" aria-hidden="true" />}
        <span className="sidebar__label">{item.label}</span>
      </NavLink>
    );
  }, [location.pathname, onClose]);

  const handleLogout = () => {
    clearAuth();
    setUserMenuOpen(false);
    navigate('/login');
  };

  const openUserMenu = useCallback(() => {
    clearTimeout(timerRef.current);
    if (userMenuRef.current) setAnchorRect(userMenuRef.current.getBoundingClientRect());
  }, []);

  const closeUserMenu = useCallback(() => {
    timerRef.current = setTimeout(() => setAnchorRect(null), 100);
  }, []);

  const handleUserMenuClose = useCallback(() => {
    clearTimeout(timerRef.current);
    setAnchorRect(null);
    setUserMenuOpen(false);
  }, []);

  return (
    <>
      {isOpen && <div className="sidebar-overlay" onClick={onClose} aria-hidden="true" />}

      <nav
        className={clsx('sidebar', { 'sidebar--open': isOpen, 'sidebar--collapsed': isCollapsed })}
        aria-label="Main navigation"
      >
        {/* toggle button */}
        <div className="sidebar__header">
          <button
            className="sidebar__toggle"
            onClick={onToggleCollapse}
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </button>
        </div>

        <div className="sidebar__inner">
          {isCollapsed ? (
            /* ── Collapsed: one icon per group ── */
            <ul className="sidebar__collapsed-nav">
              {visibleGroups.map((group) => (
                <CollapsedGroupItem
                  key={group.id}
                  group={group}
                  pathname={location.pathname}
                  onLinkClick={() => window.innerWidth < 768 && onClose()}
                />
              ))}
            </ul>
          ) : (
            /* ── Expanded: accordion ── */
            <ul className="sidebar__accordion">
              {visibleGroups.map((group) => {
                const isExpanded = expandedGroupId === group.id;
                const hasActiveItem = group.items.some((item) => isItemActive(item, location.pathname));
                return (
                  <li key={group.id} className="sidebar__group">
                    <button
                      type="button"
                      className={clsx('sidebar__group-toggle', {
                        'sidebar__group-toggle--active': hasActiveItem,
                        'sidebar__group-toggle--open': isExpanded,
                      })}
                      onClick={() => setExpandedGroupId((c) => c === group.id ? null : group.id)}
                      aria-expanded={isExpanded}
                      aria-controls={`sidebar-group-${group.id}`}
                    >
                      <span className="sidebar__group-label">{group.label}</span>
                      <ChevronRight
                        className={clsx('sidebar__group-chevron', { 'sidebar__group-chevron--open': isExpanded })}
                        size={16}
                        aria-hidden="true"
                      />
                    </button>

                    {isExpanded && (
                      <ul id={`sidebar-group-${group.id}`} className="sidebar__group-panel">
                        {group.items.map((item) => (
                          <li key={item.path}>{renderLink(item)}</li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="sidebar__footer">
          {user && !isCollapsed && (
            <div className="sidebar__du">
              {user.du_assigned && (
                <span className="sidebar__du-label">DU: {user.du_assigned}</span>
              )}
            </div>
          )}
          <div className="sidebar__footer-controls">
            <button
              className="sidebar__footer-icon-btn"
              onClick={() => navigate('/notifications')}
              aria-label={`Notifications, ${notificationData.unreadCount || 0} unread`}
              title="Notifications"
            >
              <Bell size={18} aria-hidden="true" />
              {(notificationData.unreadCount || 0) > 0 && (
                <span className="sidebar__footer-badge" aria-hidden="true">
                  {(notificationData.unreadCount || 0) > 9 ? '9+' : notificationData.unreadCount}
                </span>
              )}
            </button>

            <div
              className="sidebar__footer-user"
              ref={userMenuRef}
              onMouseEnter={openUserMenu}
              onMouseLeave={closeUserMenu}
            >
              <button
                className="sidebar__footer-user-btn"
                onClick={() => setUserMenuOpen((o) => !o)}
                aria-haspopup="true"
                aria-expanded={userMenuOpen}
                aria-label={`User menu for ${user?.email}`}
                title={user?.email}
              >
                <span className="sidebar__footer-user-initials">
                  {getInitials(user?.email || '')}
                </span>
              </button>

              {anchorRect && (
                <UserMenuDropdown
                  anchorRect={anchorRect}
                  user={user}
                  onLogout={handleLogout}
                  onClose={handleUserMenuClose}
                />
              )}
            </div>
          </div>
        </div>
      </nav>
    </>
  );
}

export default Sidebar;